import { parseClassificationLayer } from "./classificationPlanConfigOptions";

export type DraftItemFieldKey =
  | "assessmentName"
  | "objectType"
  | "objectSubType"
  | "conceptCode"
  | "conceptValues"
  | "assessmentPrompt"
  | "taggingApproach"
  | "execution"
  | "layer"
  | "assessmentTemplate";

export type DraftItemFieldErrors = Partial<Record<DraftItemFieldKey, string>>;

type DraftItemLike = {
  /** Required on Classification Add/Edit forms. */
  assessmentName?: string;
  objectType: string;
  objectSubType: string;
  objectTypeName?: string;
  conceptCode: string;
  conceptValues: unknown[];
  assessmentPrompt: string;
  allValues?: boolean;
  /** Hidden for now — kept on the type for submit/edit hydrate. */
  taggingApproach?: string;
  /** Hidden for now — Layer replaces Execution for run order. */
  execution?: string;
  layer?: string;
  assessmentTemplate?: string;
};

const REQUIRED_MESSAGES = {
  assessmentName: "Assessment Name is required.",
  // Cascade field labels: Reference Items / Impacted Items (not raw API terms).
  objectType: "Please select Reference Items.",
  objectSubType: "Please select Reference Items.",
  conceptCode: "Please select Impacted Items.",
  conceptValues: "Please select at least one Impacted Item value.",
  layer: "Layer is required.",
  assessmentTemplate: "This field is required.",
} as const satisfies Partial<Record<DraftItemFieldKey, string>>;

const LAYER_INVALID_MESSAGE = "Layer must be an integer between 0 and 10.";

export const PLAN_LIMIT_CONCEPT_ERROR =
  "This dimension has reached the maximum plan limit.";

export function validateDraftItem(
  item: DraftItemLike,
  options?: { requireObjectSubType?: boolean },
): DraftItemFieldErrors {
  const errors: DraftItemFieldErrors = {};
  const requireObjectSubType = options?.requireObjectSubType ?? true;
  const assessmentTemplate = (item.assessmentTemplate ?? "").trim();

  if (!(item.assessmentName ?? "").trim()) {
    errors.assessmentName = REQUIRED_MESSAGES.assessmentName;
  }
  if (!item.objectType.trim()) {
    errors.objectType = REQUIRED_MESSAGES.objectType;
  }
  if (requireObjectSubType && !item.objectSubType.trim()) {
    errors.objectSubType = REQUIRED_MESSAGES.objectSubType;
  }
  if (!item.conceptCode.trim()) {
    errors.conceptCode = REQUIRED_MESSAGES.conceptCode;
  } else if (!item.allValues && item.conceptValues.length === 0) {
    errors.conceptValues = REQUIRED_MESSAGES.conceptValues;
  }

  // Tagging Approach / Execution UI is temporarily hidden — not validated.

  const layerRaw = (item.layer ?? "").trim();
  if (!layerRaw) {
    errors.layer = REQUIRED_MESSAGES.layer;
  } else if (parseClassificationLayer(layerRaw) === null) {
    errors.layer = LAYER_INVALID_MESSAGE;
  }
  if (!assessmentTemplate) {
    errors.assessmentTemplate = REQUIRED_MESSAGES.assessmentTemplate;
  }

  return errors;
}

export function hasDraftItemFieldErrors(
  errors: DraftItemFieldErrors,
): boolean {
  return Object.keys(errors).length > 0;
}
